/**
 * InsuranceCardViewer - Universal dialog for viewing insurance card images
 * 
 * Resolves GCS paths, local paths, and direct URLs into viewable images.
 * Used by both ClientInsuranceDialog and App.js client chart.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Loader2, ImageOff, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function InsuranceCardViewer({ isOpen, onClose, cardData }) {
  // cardData: { type: 'Primary', front: 'gcs://...', back: 'gcs://...' }
  const [resolvedFront, setResolvedFront] = useState(null);
  const [resolvedBack, setResolvedBack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [frontError, setFrontError] = useState(false);
  const [backError, setBackError] = useState(false);

  useEffect(() => {
    if (!isOpen || !cardData) return;
    
    setFrontError(false);
    setBackError(false);
    setResolvedFront(null);
    setResolvedBack(null);
    
    const resolve = async () => {
      setLoading(true);
      try {
        const resolveUrl = async (url) => {
          if (!url) return null;
          if (url.startsWith('http')) return url;
          if (url.startsWith('gcs://') || url.startsWith('/app/') || url.startsWith('/api/')) {
            const resp = await axios.post(`${API}/api/insurance-card/resolve`, { gcs_path: url });
            return resp.data.url;
          }
          return url;
        };
        
        const [front, back] = await Promise.all([
          resolveUrl(cardData.front),
          resolveUrl(cardData.back),
        ]);
        setResolvedFront(front);
        setResolvedBack(back);
      } catch (err) {
        console.error('Failed to resolve insurance card URLs:', err);
      } finally {
        setLoading(false);
      }
    };
    
    resolve();
  }, [isOpen, cardData]);

  const hasCards = resolvedFront || resolvedBack;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="insurance-card-viewer">
        <DialogHeader>
          <DialogTitle>{cardData?.type || 'Insurance'} Card</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            <span className="ml-3 text-gray-500">Loading card images...</span>
          </div>
        ) : !hasCards && !cardData?.front && !cardData?.back ? (
          <p className="text-center py-8 text-gray-500" data-testid="no-cards-message">
            No insurance card images available
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
            {cardData?.front && (
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-2">Front of Card</p>
                {frontError ? (
                  <div className="flex flex-col items-center justify-center border rounded-lg bg-gray-50 py-12" data-testid="front-card-error">
                    <ImageOff className="h-10 w-10 text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Could not load front image</p>
                  </div>
                ) : resolvedFront ? (
                  <img
                    src={resolvedFront}
                    alt="Insurance card front"
                    className="w-full border rounded-lg shadow-sm"
                    data-testid="front-card-image"
                    onError={() => setFrontError(true)}
                  />
                ) : (
                  <div className="flex items-center justify-center border rounded-lg bg-gray-50 py-12">
                    <p className="text-sm text-gray-400">Resolving image...</p>
                  </div>
                )}
              </div>
            )}
            {cardData?.back && (
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-2">Back of Card</p>
                {backError ? (
                  <div className="flex flex-col items-center justify-center border rounded-lg bg-gray-50 py-12" data-testid="back-card-error">
                    <ImageOff className="h-10 w-10 text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Could not load back image</p>
                  </div>
                ) : resolvedBack ? (
                  <img
                    src={resolvedBack}
                    alt="Insurance card back"
                    className="w-full border rounded-lg shadow-sm"
                    data-testid="back-card-image"
                    onError={() => setBackError(true)}
                  />
                ) : (
                  <div className="flex items-center justify-center border rounded-lg bg-gray-50 py-12">
                    <p className="text-sm text-gray-400">Resolving image...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onClose(false)} data-testid="close-card-viewer">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default InsuranceCardViewer;
